import * as THREE from 'three';

/**
 * PROCEDURAL AIRFRAME GENERATION // KEVIN ARTHUR DE LIMA
 * Creates a precision technical CAD-style airframe with solid dark fills and crisp edge lines.
 */

// Helper: Wrap geometry in solid mesh + edge line segments
export function createPart(geometry, solidMat, edgeMat) {
  const group = new THREE.Group();
  const mesh = new THREE.Mesh(geometry, solidMat);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  group.add(mesh);

  const edges = new THREE.EdgesGeometry(geometry, 25);
  const lines = new THREE.LineSegments(edges, edgeMat);
  group.add(lines);

  return { group, mesh, lines };
}

export function buildProceduralAirframe(materials) {
  const root = new THREE.Group();
  root.name = "AirframeRoot";

  // Subsystem container groups for exploded view and morphs
  const groups = {
    coreFuselage: new THREE.Group(),
    armsAndMotors: new THREE.Group(),
    escStack: new THREE.Group(),
    flightController: new THREE.Group(),
    companionComputer: new THREE.Group(),
    batteryPack: new THREE.Group(),
    payloadBay: new THREE.Group(),
    vtolWings: new THREE.Group(),
    vtolBooms: new THREE.Group(),
    vtolPusher: new THREE.Group(),
    microDucts: new THREE.Group(),
    landingPad: new THREE.Group(),
    propellers: [] // References for continuous spin
  };

  Object.values(groups).forEach(g => {
    if (g instanceof THREE.Group) root.add(g);
  });

  // 1. CORE FUSELAGE (Hexagonal carbon plates + standoffs)
  const plateShape = new THREE.Shape();
  const hexRadius = 1.2;
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI) / 3;
    const x = hexRadius * Math.cos(angle);
    const y = hexRadius * Math.sin(angle);
    if (i === 0) plateShape.moveTo(x, y);
    else plateShape.lineTo(x, y);
  }
  plateShape.closePath();

  const plateGeo = new THREE.ExtrudeGeometry(plateShape, {
    depth: 0.08,
    bevelEnabled: false
  });
  plateGeo.rotateX(Math.PI / 2);

  // Top plate
  const topPlate = createPart(plateGeo, materials.solidDark, materials.edgeLine);
  topPlate.group.position.y = 0.25;
  groups.coreFuselage.add(topPlate.group);

  // Bottom plate
  const bottomPlate = createPart(plateGeo, materials.solidDark, materials.edgeLine);
  bottomPlate.group.position.y = -0.25;
  groups.coreFuselage.add(bottomPlate.group);

  // Standoff spacers (4 brass/aluminum pillars)
  const standoffGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.5, 8);
  const standoffCoords = [
    [0.6, 0.6], [-0.6, 0.6], [0.6, -0.6], [-0.6, -0.6]
  ];
  standoffCoords.forEach(([sx, sz]) => {
    const standoff = createPart(standoffGeo, materials.solidAccent, materials.edgeLine);
    standoff.group.position.set(sx, 0, sz);
    groups.coreFuselage.add(standoff.group);
  });

  // 2. ARMS & MOTORS (6 radial hexacopter arms)
  const armLength = 3.2;
  const armGeo = new THREE.CylinderGeometry(0.08, 0.08, armLength, 8);
  armGeo.rotateZ(Math.PI / 2);

  const motorStatorGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.3, 16);
  const motorMountGeo = new THREE.BoxGeometry(0.8, 0.12, 0.6);
  const propGeo = new THREE.BoxGeometry(3.6, 0.02, 0.25);
  const propDiscGeo = new THREE.TorusGeometry(1.8, 0.015, 4, 32);
  propDiscGeo.rotateX(Math.PI / 2);

  for (let i = 0; i < 6; i++) {
    const armGroup = new THREE.Group();
    const angle = (i * Math.PI) / 3;

    // Carbon tube arm
    const arm = createPart(armGeo, materials.solidDark, materials.edgeLine);
    arm.group.position.x = armLength / 2;
    armGroup.add(arm.group);

    // Motor mount with active vents
    const mount = createPart(motorMountGeo, materials.solidAccent, materials.edgeLine);
    mount.group.position.set(armLength, -0.06, 0);
    armGroup.add(mount.group);

    // Motor body
    const motor = createPart(motorStatorGeo, materials.solidDark, materials.edgeLine);
    motor.group.position.set(armLength, 0.15, 0);
    armGroup.add(motor.group);

    // Propeller assembly
    const propGroup = new THREE.Group();
    propGroup.position.set(armLength, 0.35, 0);

    const propBlade = createPart(propGeo, materials.solidDark, materials.edgeLine);
    propGroup.add(propBlade.group);

    const propDisc = new THREE.Line(propDiscGeo, materials.propLine);
    propGroup.add(propDisc);

    armGroup.add(propGroup);
    groups.propellers.push({
      group: propGroup,
      direction: i % 2 === 0 ? 1 : -1,
      speed: 0
    });

    armGroup.rotation.y = angle;
    groups.armsAndMotors.add(armGroup);
  }

  // 3. ESC STACK (Layered power distribution with heatsink fins)
  const escPcbGeo = new THREE.BoxGeometry(0.9, 0.05, 0.9);
  const escPcb = createPart(escPcbGeo, materials.solidAccent, materials.edgeLine);
  escPcb.group.position.y = -0.08;
  groups.escStack.add(escPcb.group);

  // Heatsink fins
  for (let f = -0.3; f <= 0.3; f += 0.12) {
    const finGeo = new THREE.BoxGeometry(0.04, 0.1, 0.8);
    const fin = createPart(finGeo, materials.solidDark, materials.edgeLine);
    fin.group.position.set(f, 0.02, 0);
    groups.escStack.add(fin.group);
  }

  // 4. FLIGHT CONTROLLER (Navio2 / Pixhawk form factor with orientation arrow)
  const fcGeo = new THREE.BoxGeometry(0.8, 0.15, 0.8);
  const fc = createPart(fcGeo, materials.solidAccent, materials.edgeLine);
  fc.group.position.y = 0.12;
  groups.flightController.add(fc.group);

  // Orientation arrow on FC
  const arrowShape = new THREE.Shape();
  arrowShape.moveTo(0, 0.25);
  arrowShape.lineTo(0.2, -0.15);
  arrowShape.lineTo(0.06, -0.15);
  arrowShape.lineTo(0.06, -0.3);
  arrowShape.lineTo(-0.06, -0.3);
  arrowShape.lineTo(-0.06, -0.15);
  arrowShape.lineTo(-0.2, -0.15);
  arrowShape.closePath();
  const arrowGeo = new THREE.ShapeGeometry(arrowShape);
  arrowGeo.rotateX(-Math.PI / 2);
  const arrowMesh = new THREE.Mesh(arrowGeo, materials.edgeHighlight);
  arrowMesh.position.y = 0.21;
  groups.flightController.add(arrowMesh);

  // 5. COMPANION COMPUTER (Raspberry Pi 5 / Hailo-8L NPU form factor)
  const ccPcbGeo = new THREE.BoxGeometry(1.1, 0.06, 0.85);
  const ccPcb = createPart(ccPcbGeo, materials.solidAccent, materials.edgeLine);
  ccPcb.group.position.y = 0.35;
  groups.companionComputer.add(ccPcb.group);

  // Ports: Ethernet & USB block
  const portGeo = new THREE.BoxGeometry(0.35, 0.2, 0.28);
  const portBlock = createPart(portGeo, materials.solidDark, materials.edgeLine);
  portBlock.group.position.set(0.35, 0.48, 0.25);
  groups.companionComputer.add(portBlock.group);

  // Hailo-8L / CPU heatsink
  const cpuHeatsinkGeo = new THREE.BoxGeometry(0.32, 0.12, 0.32);
  const cpuHeatsink = createPart(cpuHeatsinkGeo, materials.solidDark, materials.edgeHighlight);
  cpuHeatsink.group.position.set(-0.15, 0.44, -0.1);
  groups.companionComputer.add(cpuHeatsink.group);

  // 6. BATTERY PACK (6S 22.2V LiPo block)
  const batteryGeo = new THREE.BoxGeometry(1.2, 0.7, 1.8);
  const battery = createPart(batteryGeo, materials.solidBattery, materials.edgeLine);
  battery.group.position.y = -0.75;
  groups.batteryPack.add(battery.group);

  // Battery strap
  const strapGeo = new THREE.BoxGeometry(1.25, 0.72, 0.3);
  const strap = createPart(strapGeo, materials.solidAccent, materials.edgeLine);
  strap.group.position.y = -0.75;
  groups.batteryPack.add(strap.group);

  // 7. PAYLOAD BAY (Sensors / Gimbal Pod / Umbilical Connector)
  const payloadGeo = new THREE.SphereGeometry(0.45, 12, 8);
  const payload = createPart(payloadGeo, materials.solidDark, materials.edgeLine);
  payload.group.position.y = -1.45;
  groups.payloadBay.add(payload.group);

  const sensorLensGeo = new THREE.CylinderGeometry(0.18, 0.18, 0.25, 12);
  sensorLensGeo.rotateX(Math.PI / 2);
  const sensorLens = createPart(sensorLensGeo, materials.solidAccent, materials.edgeHighlight);
  sensorLens.group.position.set(0, -1.45, 0.38);
  groups.payloadBay.add(sensorLens.group);

  // 8. VTOL QUADPLANE COMPONENTS (For A.E.R.I.S. & Prometheus Scout morph)
  // Main Wing (carbon spars, high aspect ratio aerofoil profile)
  const wingGeo = new THREE.BoxGeometry(11.0, 0.12, 1.4);
  const wing = createPart(wingGeo, materials.solidDark, materials.edgeLine);
  wing.group.position.set(0, 0.4, 0);
  groups.vtolWings.add(wing.group);

  // Twin tail booms
  [-2.2, 2.2].forEach(bx => {
    const boomGeo = new THREE.CylinderGeometry(0.08, 0.08, 5.0, 8);
    boomGeo.rotateX(Math.PI / 2);
    const boom = createPart(boomGeo, materials.solidDark, materials.edgeLine);
    boom.group.position.set(bx, 0.35, -1.2);
    groups.vtolBooms.add(boom.group);

    // Vertical fin / rudder
    const rudderGeo = new THREE.BoxGeometry(0.06, 1.2, 0.8);
    const rudder = createPart(rudderGeo, materials.solidDark, materials.edgeLine);
    rudder.group.position.set(bx, 0.9, -3.4);
    groups.vtolBooms.add(rudder.group);
  });

  // Horizontal stabilizer elevator
  const elevatorGeo = new THREE.BoxGeometry(4.8, 0.06, 0.7);
  const elevator = createPart(elevatorGeo, materials.solidDark, materials.edgeLine);
  elevator.group.position.set(0, 1.4, -3.4);
  groups.vtolBooms.add(elevator.group);

  // Forward pusher motor & propeller
  const pusherMotorGeo = new THREE.CylinderGeometry(0.25, 0.25, 0.35, 12);
  pusherMotorGeo.rotateX(Math.PI / 2);
  const pusherMotor = createPart(pusherMotorGeo, materials.solidAccent, materials.edgeLine);
  pusherMotor.group.position.set(0, 0.4, -0.9);
  groups.vtolPusher.add(pusherMotor.group);

  const pusherPropGeo = new THREE.BoxGeometry(2.4, 0.2, 0.02);
  const pusherProp = createPart(pusherPropGeo, materials.solidDark, materials.edgeHighlight);
  pusherProp.group.position.set(0, 0.4, -1.1);
  groups.vtolPusher.add(pusherProp.group);
  groups.propellers.push({
    group: pusherProp.group,
    direction: 1,
    speed: 0
  });

  // Initially hide VTOL-specific parts for baseline Hexacopter hero state
  groups.vtolWings.visible = false;
  groups.vtolBooms.visible = false;
  groups.vtolPusher.visible = false;

  // 9. MICRO DUCTS (For ESP32 Micro-Drone morph)
  const ductGeo = new THREE.CylinderGeometry(0.85, 0.85, 0.4, 16, 1, true);
  [[-1.0, -1.0], [1.0, -1.0], [-1.0, 1.0], [1.0, 1.0]].forEach(([dx, dz]) => {
    const duct = createPart(ductGeo, materials.solidAccent, materials.edgeHighlight);
    duct.group.position.set(dx, 0.1, dz);
    groups.microDucts.add(duct.group);
  });
  groups.microDucts.visible = false;

  // 10. LANDING PAD (DWG-006 Contact section touchdown target)
  const padRingGeo = new THREE.RingGeometry(2.8, 3.0, 32);
  padRingGeo.rotateX(-Math.PI / 2);
  const padRing = new THREE.Line(new THREE.EdgesGeometry(padRingGeo), materials.edgeLine);
  padRing.position.y = -2.2;
  groups.landingPad.add(padRing);

  const padCrosshairGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-3.5, -2.2, 0), new THREE.Vector3(3.5, -2.2, 0),
    new THREE.Vector3(0, -2.2, -3.5), new THREE.Vector3(0, -2.2, 3.5)
  ]);
  const padCrosshair = new THREE.LineSegments(padCrosshairGeo, materials.edgeLine);
  groups.landingPad.add(padCrosshair);

  // 11. GROUND GRID & HORIZON
  const gridHelper = new THREE.GridHelper(50, 50, 0x333333, 0x1A1A1A);
  gridHelper.position.y = -2.25;
  root.add(gridHelper);

  const horizonGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-60, -2.25, -25), new THREE.Vector3(60, -2.25, -25)
  ]);
  const horizonLine = new THREE.Line(horizonGeo, materials.gridLine);
  root.add(horizonLine);

  return { root, groups };
}
